import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, differenceInCalendarDays, isPast, isValid } from "date-fns";
import Page from "../molecules/Page";
import PasswordInput from "../atoms/PasswordInput";
import api from "../../api/apiHelper";
import { persistDashboardData } from "../../utils/apiCache";
import { useDomainSwitch } from "../../utils/domain";
import SessionSetupPopup from "../molecules/SessionSetupPopup";
import DomainLevelCard from "../molecules/DomainLevelCard";

import {
  MdOutlineCalendarToday,
  MdCheckCircle,
  MdPerson,
  MdKey,
  MdWarningAmber,
  MdOutlineEmail,
  MdEmail,
  MdLogout,
  MdVolumeUp,
  MdVolumeOff,
  MdVibration,
  MdInsights,
} from 'react-icons/md';
import { FiChevronRight, FiSend, FiCheck } from 'react-icons/fi';
import { shareMatematly } from '../../utils/shareMatematly.js';

// ==============================================================================
// KOMPONENTY WIZUALNE
// ==============================================================================

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-1">
    {children}
  </p>
);

const ToggleSwitch = ({ enabled }) => (
  <div className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${enabled ? 'bg-[#233855]' : 'bg-gray-300'}`}>
    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </div>
);

// Wiersz informacyjny — czytelny, statyczny, bez hover
const InfoRow = ({ iconBg, icon, title, children }) => (
  <div className="flex items-start gap-3 p-3.5 bg-white rounded-xl border border-gray-100 shadow-none">
    <div className={`flex-shrink-0 p-2 rounded-xl shadow-sm ${iconBg}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-1">{title}</p>
      {children}
    </div>
  </div>
);

// Wiersz klikalny nawigacyjny — ze strzałką, wyraźny hover
const ActionRow = ({ iconBg, icon, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left p-3.5 bg-white rounded-xl border border-gray-100 hover:shadow-[0_2px_8px_rgba(35,56,85,0.08)] hover:border-[#233855]/15 active:scale-[0.98] transition-all duration-150 group flex items-center gap-3"
  >
    <div className={`flex-shrink-0 p-2 rounded-xl shadow-sm ${iconBg}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm text-[#233855] leading-snug">{title}</p>
      {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
      <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
    </div>
  </button>
);

// Wiersz „Poleć Matematly" — natywny share / kopiowanie linku z feedbackiem
const ShareRow = () => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const result = await shareMatematly();
    if (result === 'copied') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="w-full text-left p-3.5 bg-white rounded-xl border border-gray-100 hover:shadow-[0_2px_8px_rgba(35,56,85,0.08)] hover:border-[#233855]/15 active:scale-[0.98] transition-all duration-150 group flex items-center gap-3"
    >
      <div className="flex-shrink-0 p-2 rounded-xl shadow-sm bg-[#233855]">
        {copied
          ? <FiCheck className="text-[#007D00]" size={16} />
          : <FiSend className="text-[#E09F2E]" size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-[#233855] leading-snug">Poleć Matematly znajomemu</p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {copied ? 'Skopiowano link ✓' : 'Wyślij komuś, komu może pomóc'}
        </p>
      </div>
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
        <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all duration-150" />
      </div>
    </button>
  );
};

// Wiersz klikalny z przełącznikiem — ze switchem, wyraźny hover
const ToggleRow = ({ iconBg, icon, title, subtitle, enabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left p-3.5 bg-white rounded-xl border border-gray-100 hover:shadow-[0_2px_8px_rgba(35,56,85,0.08)] hover:border-[#233855]/15 active:scale-[0.98] transition-all duration-150 group flex items-center gap-3"
  >
    <div className={`flex-shrink-0 p-2 rounded-xl shadow-sm ${iconBg}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm text-[#233855] leading-snug">{title}</p>
      {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    <ToggleSwitch enabled={enabled} />
  </button>
);

// Sekcja rozwijana — spójna z ActionRow
const CollapsibleSection = ({ iconBg, icon, title, subtitle, isOpen, onToggle, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left p-3.5 group flex items-center gap-3 active:scale-[0.99] transition-all"
    >
      <div className={`flex-shrink-0 p-2 rounded-xl shadow-sm ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-[#233855] leading-snug">{title}</p>
        {!isOpen && subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
        <FiChevronRight className={`w-4 h-4 transition-all duration-200 ${isOpen ? 'rotate-90 text-gray-500' : 'text-gray-400 group-hover:text-[#233855]'}`} />
      </div>
    </button>
    {isOpen && (
      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
        {children}
      </div>
    )}
  </div>
);

// ==============================================================================
// MODALE (bez zmian)
// ==============================================================================

const SuccessModal = ({ isOpen, onClose, newExpiryDate }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <MdCheckCircle className="h-16 w-16 text-[#007D00] mx-auto mb-4" />
        <h3 className="text-xl font-apotek tracking-widest text-[#233855]">SUKCES!</h3>
        <p className="mt-3 text-sm text-gray-600">Twój dostęp został pomyślnie przedłużony do:</p>
        <p className="mt-2 text-2xl font-bold text-[#233855]">{newExpiryDate}</p>
        <button onClick={onClose} className="mt-6 w-full px-6 py-3 text-sm font-apotek tracking-widest rounded-lg bg-[#233855] text-white hover:bg-[#345081] shadow-md transition-colors">SUPER!</button>
      </div>
    </div>
  );
};

const DeleteAccountModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    setError("");
    try {
      await api.post('delete-account/');
      localStorage.removeItem("token");
      navigate("/login", { replace: true, state: { message: "Twoje konto zostało pomyślnie usunięte." } });
    } catch (err) {
      setError(err.response?.data?.error || "Wystąpił błąd. Spróbuj ponownie.");
      setIsLoading(false);
    }
  };

  useEffect(() => { if (!isOpen) { setError(""); setIsLoading(false); } }, [isOpen]);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <MdWarningAmber className="h-16 w-16 text-[#D9534F] mx-auto mb-4" />
        <h3 className="text-xl font-apotek tracking-widest text-[#233855] text-center">Usuwanie konta</h3>
        <p className="mt-3 text-sm text-gray-600 text-center">Czy na pewno chcesz trwale usunąć swoje konto?</p>
        <p className="mt-2 text-xs text-gray-500 text-center">Wszystkie Twoje dane osobowe zostaną zanonimizowane. <strong>Tej operacji nie można cofnąć.</strong></p>
        {error && <p className="mt-3 text-xs text-center text-[#8A0000]">{error}</p>}
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
          <button type="button" onClick={onClose} disabled={isLoading} className="w-full sm:w-auto px-6 py-3 text-sm font-apotek tracking-widest rounded-lg bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 transition-colors disabled:opacity-50">ANULUJ</button>
          <button type="button" onClick={handleConfirmDelete} disabled={isLoading} className="w-full sm:w-auto px-6 py-3 text-sm font-apotek tracking-widest rounded-lg bg-[#D9534F] text-white hover:bg-[#C9302C] shadow-md transition-colors disabled:opacity-50">{isLoading ? "USUWANIE..." : "TAK, USUŃ KONTO"}</button>
        </div>
      </div>
    </div>
  );
};

const MarketingConsentModal = ({ isOpen, onClose, onConfirm, isEnabling, isLoading }) => {
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  useEffect(() => { if (isOpen) { setMarketingChecked(false); setPrivacyChecked(false); } }, [isOpen]);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        {isEnabling
          ? <MdEmail className="h-16 w-16 text-[#007D00] mx-auto mb-4" />
          : <MdOutlineEmail className="h-16 w-16 text-[#E09F2E] mx-auto mb-4" />
        }
        <h3 className="text-xl font-apotek tracking-widest text-[#233855] text-center">{isEnabling ? "Włącz komunikację" : "Wyłącz powiadomienia"}</h3>
        <div className="mt-4 space-y-4">
          {isEnabling ? (
            <>
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                <label className="flex items-start cursor-pointer">
                  <input type="checkbox" checked={marketingChecked} onChange={(e) => setMarketingChecked(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] mt-0.5" />
                  <div className="ml-3 text-[0.7rem] text-gray-600 text-justify">Wyrażam zgodę na używanie mojego adresu e-mail przez MATEMATLY sp. z o.o. do celów przesyłania informacji handlowej...</div>
                </label>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                <label className="flex items-start cursor-pointer">
                  <input type="checkbox" checked={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#233855] mt-0.5" />
                  <div className="ml-3 text-[0.7rem] text-gray-600 text-justify">Potwierdzam, że zapoznałem się z polityką prywatności.</div>
                </label>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-600 text-center">Czy na pewno chcesz zrezygnować?</p>
          )}
        </div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
          <button type="button" onClick={onClose} disabled={isLoading} className="w-full sm:w-auto px-6 py-3 text-sm font-apotek tracking-widest rounded-lg bg-white text-gray-700 border border-gray-300">ANULUJ</button>
          <button type="button" onClick={onConfirm} disabled={isLoading || (isEnabling && (!marketingChecked || !privacyChecked))} className={`w-full sm:w-auto px-6 py-3 text-sm font-apotek tracking-widest rounded-lg text-white shadow-md disabled:opacity-40 ${isEnabling ? 'bg-[#233855]' : 'bg-[#E09F2E]'}`}>{isLoading ? "ZAPISYWANIE..." : "ZATWIERDŹ"}</button>
        </div>
      </div>
    </div>
  );
};

// ==============================================================================
// GŁÓWNY KOMPONENT
// ==============================================================================

const UserSettings = () => {
  const [emailMarketingConsent, setEmailMarketingConsent] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [activeDomain, setActiveDomain] = useState('PP');
  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const { start: startDomainSwitch, overlayProps: domainSwitchOverlay } = useDomainSwitch();
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('sound_enabled') !== 'false');
  const [vibrationEnabled, setVibrationEnabled] = useState(() => localStorage.getItem('vibration_enabled') !== 'false');
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);

  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordHints, setPasswordHints] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [accessExpiryInfo, setAccessExpiryInfo] = useState({
    date: null,
    daysRemaining: null,
    isExpired: null,
    isUnlimited: null,
    exactTimeLeft: null,
  });

  // Rozszerzenie to osobny produkt z własną datą — pokazujemy je obok podstawy.
  const [extendedExpiryInfo, setExtendedExpiryInfo] = useState({
    date: null,
    isExpired: null,
    exactTimeLeft: null,
  });
  const extendedHasAccess = !!extendedExpiryInfo.date && !extendedExpiryInfo.isExpired;

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [modalExpiryDate, setModalExpiryDate] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [isMarketingLoading, setIsMarketingLoading] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchUserData = async () => {
    try {
      const res = await api.get('user_dashboard_data/');
      persistDashboardData(res.data);
      setUsername(res.data.username);
      setUserId(res.data.id || res.data.user_id);

      if (res.data.account_status) {
        const timestamp = res.data.account_status.access_expires_at_timestamp;
        if (timestamp === null || typeof timestamp === 'undefined') {
          setAccessExpiryInfo({ date: "Nieograniczony", daysRemaining: null, isExpired: false, isUnlimited: true, exactTimeLeft: null });
        } else {
          const expiryDateObj = new Date(timestamp * 1000);
          if (isValid(expiryDateObj)) {
            const today = new Date();
            const daysLeft = differenceInCalendarDays(expiryDateObj, today);
            const expired = isPast(expiryDateObj);

            let exactTimeStr = null;
            const diffMs = expiryDateObj - today;
            if (diffMs > 0) {
              const d = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const h = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
              const m = Math.floor((diffMs / (1000 * 60)) % 60);
              if (d > 0) exactTimeStr = `${d} dni i ${h} h`;
              else if (h > 0) exactTimeStr = `${h} h i ${m} min`;
              else exactTimeStr = `${m} minut`;
            }

            setAccessExpiryInfo({
              date: format(expiryDateObj, "yyyy-MM-dd"),
              daysRemaining: daysLeft,
              isExpired: expired,
              isUnlimited: false,
              exactTimeLeft: exactTimeStr,
            });
          }
        }
        // Patrz Upgrade.js — `pr_expires_at` jest bez maski, stare pole to fallback.
        const extendedTs = res.data.account_status.pr_expires_at
          ?? res.data.account_status.access_expires_at_pr_timestamp;
        if (extendedTs) {
          const extendedDateObj = new Date(extendedTs * 1000);
          if (isValid(extendedDateObj)) {
            const diffMs = extendedDateObj - new Date();
            let extendedTimeStr = null;
            if (diffMs > 0) {
              const d = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const h = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
              const m = Math.floor((diffMs / (1000 * 60)) % 60);
              if (d > 0) extendedTimeStr = `${d} dni i ${h} h`;
              else if (h > 0) extendedTimeStr = `${h} h i ${m} min`;
              else extendedTimeStr = `${m} minut`;
            }
            setExtendedExpiryInfo({
              date: format(extendedDateObj, "yyyy-MM-dd"),
              isExpired: isPast(extendedDateObj),
              exactTimeLeft: extendedTimeStr,
            });
          }
        } else {
          setExtendedExpiryInfo({ date: null, isExpired: null, exactTimeLeft: null });
        }

        setEmailMarketingConsent(res.data.account_status.email_marketing_consent || false);
        setAnalyticsConsent(res.data.account_status.analytics_allowed || false);
        setActiveDomain(res.data.account_status.active_domain || 'PP');
      }
    } catch (err) {
      console.error("Błąd pobierania danych użytkownika:", err);
    }
  };

  useEffect(() => { if (!token) navigate("/login"); else fetchUserData(); }, [token, navigate]);

  const handleConfirmMarketing = async () => {
    setIsMarketingLoading(true);
    try {
      await api.post('/update-email-consent/', { consent: !emailMarketingConsent });
      setEmailMarketingConsent(!emailMarketingConsent);
      setIsMarketingModalOpen(false);
    } catch (error) {
      alert("Błąd.");
    } finally {
      setIsMarketingLoading(false);
    }
  };

  const handleToggleAnalytics = async () => {
    if (isAnalyticsLoading) return;
    const newValue = !analyticsConsent;
    setIsAnalyticsLoading(true);
    try {
      await api.post('/update-analytics-consent/', { consent: newValue });
      setAnalyticsConsent(newValue);
    } catch (error) {
      alert("Błąd.");
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const handleSelectActiveDomain = async (newDomain) => {
    if (isDomainLoading || newDomain === activeDomain) return;
    setIsDomainLoading(true);
    try {
      await startDomainSwitch(newDomain);
      setActiveDomain(newDomain);
    } catch (error) {
      alert("Błąd.");
    } finally {
      setIsDomainLoading(false);
    }
  };

  const validatePassword = (pass) => {
    const hints = [];
    if (pass.length < 8) hints.push("Min. 8 znaków.");
    if (!/[A-Z]/.test(pass)) hints.push("Wielka litera.");
    if (!/[0-9]/.test(pass)) hints.push("Cyfra.");
    setPasswordHints(hints);
    return hints.length ? hints.join(" ") : null;
  };

  const handleUsernameUpdate = async () => {
    try {
      await api.post('change_username/', { new_username: newUsername });
      setUsername(newUsername);
      setNewUsername("");
      setShowUsernameForm(false);
    } catch (err) {
      console.error("Błąd zmiany nicku:", err);
    }
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    setIsSubmitting(true);
    try {
      await api.post('change_password/', { old_password: oldPassword, new_password: newPassword });
      setShowPasswordForm(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Błąd zmiany hasła:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try { await api.post('user_logout/', {}); } catch {}
    localStorage.removeItem("token");
    navigate("/login");
  };

  const accessIconBg = accessExpiryInfo.isExpired ? 'bg-[#8A0000]' : 'bg-[#007D00]';

  return (
    <Page showNav={true}>
      <SessionSetupPopup {...domainSwitchOverlay} />
      <div className="bg-white lg:bg-transparent w-full">
      <div className="flex flex-col max-w-2xl mx-auto w-full px-4 lg:px-0">
        <div className="w-full lg:px-10 py-4 lg:py-10 lg:mt-[8vh] lg:bg-white lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200">

          {/* Nagłówek */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-apotek tracking-wider text-[#233855] uppercase leading-none">Ustawienia</h1>
          </div>

          {/* SEKCJA: DOSTĘP */}
          {accessExpiryInfo.date && (
            <div className="mb-5 space-y-2">
              <SectionLabel>Dostęp</SectionLabel>

              <InfoRow
                iconBg={accessIconBg}
                icon={<MdOutlineCalendarToday className="text-white" size={16} />}
                title="Matura Podstawowa"
              >
                {accessExpiryInfo.isUnlimited ? (
                  <p className="text-sm font-semibold text-[#007D00]">Nieograniczony dostęp</p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-[#233855]">
                      Ważny do: {accessExpiryInfo.date}
                    </p>
                    {accessExpiryInfo.isExpired && (
                      <span className="inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#8A0000]/10 text-[#8A0000] border border-[#8A0000]/20">
                        Wygasł
                      </span>
                    )}
                    {!accessExpiryInfo.isExpired && accessExpiryInfo.exactTimeLeft && (
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Pozostało: <strong className="text-[#233855]">{accessExpiryInfo.exactTimeLeft}</strong>
                      </p>
                    )}
                  </>
                )}
              </InfoRow>

              {/* Rozszerzenie pokazujemy ZAWSZE — brak dostępu to też informacja,
                  a bez tej karty user nie wiedział, że drugi poziom w ogóle istnieje. */}
              <InfoRow
                iconBg={extendedHasAccess ? 'bg-[#007D00]' : 'bg-[#8A0000]'}
                icon={<MdOutlineCalendarToday className="text-white" size={16} />}
                title="Matura Rozszerzona"
              >
                {extendedHasAccess ? (
                  <>
                    <p className="text-sm font-semibold text-[#233855]">
                      Ważny do: {extendedExpiryInfo.date}
                    </p>
                    {extendedExpiryInfo.exactTimeLeft && (
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Pozostało: <strong className="text-[#233855]">{extendedExpiryInfo.exactTimeLeft}</strong>
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-[#233855]">
                      {extendedExpiryInfo.date ? `Wygasł: ${extendedExpiryInfo.date}` : 'Brak dostępu'}
                    </p>
                    <span className="inline-flex mt-1.5 items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#8A0000]/10 text-[#8A0000] border border-[#8A0000]/20">
                      Wygasł
                    </span>
                  </>
                )}
              </InfoRow>

              {!accessExpiryInfo.isUnlimited && (
                <ActionRow
                  iconBg="bg-[#233855]"
                  icon={<MdKey className="text-[#E09F2E]" size={16} />}
                  title="Przedłuż dostęp"
                  subtitle="Zobacz dostępne plany"
                  onClick={() => navigate('/upgrade')}
                />
              )}
            </div>
          )}

          {/* SEKCJA: POZIOM NAUKI */}
          {/* Ta sama karta co w modalu roadmapy — jeden wyglad, jedna logika przelaczania. */}
          <div className="mb-5 space-y-2">
            <SectionLabel>Poziom nauki</SectionLabel>

            <DomainLevelCard
              value={activeDomain}
              onSelect={handleSelectActiveDomain}
              isSaving={isDomainLoading}
            />
          </div>

          {/* SEKCJA: PREFERENCJE */}
          <div className="mb-5 space-y-2">
            <SectionLabel>Preferencje</SectionLabel>

            <ToggleRow
              iconBg={emailMarketingConsent ? 'bg-[#007D00]' : 'bg-gray-400'}
              icon={<MdOutlineEmail className="text-white" size={16} />}
              title="E-maile marketingowe"
              subtitle={emailMarketingConsent ? "Porady, nowości i promocje" : "Włącz, aby otrzymywać porady"}
              enabled={emailMarketingConsent}
              onClick={() => setIsMarketingModalOpen(true)}
            />

            <ToggleRow
              iconBg={analyticsConsent ? 'bg-[#007D00]' : 'bg-gray-400'}
              icon={<MdInsights className="text-white" size={16} />}
              title="Analityka"
              subtitle={analyticsConsent ? "Pomagasz nam ulepszać aplikację" : "Włącz, aby pomóc nam ulepszać aplikację"}
              enabled={analyticsConsent}
              onClick={handleToggleAnalytics}
            />

            <ToggleRow
              iconBg={soundEnabled ? 'bg-[#233855]' : 'bg-gray-400'}
              icon={soundEnabled
                ? <MdVolumeUp className="text-[#E09F2E]" size={16} />
                : <MdVolumeOff className="text-white" size={16} />
              }
              title="Dźwięki w aplikacji"
              subtitle={soundEnabled ? "Włączone" : "Wyłączone"}
              enabled={soundEnabled}
              onClick={() => {
                const next = !soundEnabled;
                setSoundEnabled(next);
                localStorage.setItem('sound_enabled', String(next));
              }}
            />

            <ToggleRow
              iconBg={vibrationEnabled ? 'bg-[#233855]' : 'bg-gray-400'}
              icon={<MdVibration className={vibrationEnabled ? 'text-[#E09F2E]' : 'text-white'} size={16} />}
              title="Wibracje w aplikacji"
              subtitle={vibrationEnabled ? "Włączone" : "Wyłączone"}
              enabled={vibrationEnabled}
              onClick={() => {
                const next = !vibrationEnabled;
                setVibrationEnabled(next);
                localStorage.setItem('vibration_enabled', String(next));
              }}
            />
          </div>

          {/* SEKCJA: POLEĆ ZNAJOMEMU */}
          <div className="mb-5 space-y-2">
            <SectionLabel>Poleć</SectionLabel>
            <ShareRow />
          </div>

          {/* SEKCJA: KONTO */}
          <div className="mb-6 space-y-2">
            <SectionLabel>Konto</SectionLabel>

            <CollapsibleSection
              iconBg="bg-[#233855]"
              icon={<MdPerson className="text-[#E09F2E]" size={16} />}
              title="Zarządzanie kontem"
              subtitle="Nick, hasło, usunięcie konta"
              isOpen={isUserSettingsOpen}
              onToggle={() => setIsUserSettingsOpen(p => !p)}
            >
              <div className="space-y-4 pt-1">

                {/* ID użytkownika */}
                <div className="ph-no-capture flex justify-between items-center py-2 border-b border-gray-100">
                  <p className="text-sm text-gray-600">ID: <strong className="font-mono text-gray-700">{userId || "..."}</strong></p>
                  <button
                    onClick={() => { if (userId) navigator.clipboard.writeText(userId.toString()); }}
                    className="text-xs font-medium text-gray-400 hover:text-[#233855] uppercase transition-colors"
                  >
                    Kopiuj
                  </button>
                </div>

                {/* Nick */}
                {!showUsernameForm ? (
                  <div className="ph-no-capture flex justify-between items-center">
                    <p className="text-sm text-gray-600">Nickname: <strong className="text-[#233855]">{username}</strong></p>
                    <button onClick={() => setShowUsernameForm(true)} className="text-xs font-semibold text-[#233855] hover:underline">Edytuj</button>
                  </div>
                ) : (
                  <div>
                    <input
                      className="block w-full rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#233855]/40"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      placeholder="Nowy nickname"
                    />
                    <div className="mt-2 flex gap-2">
                      <button onClick={handleUsernameUpdate} className="px-4 py-2 text-xs font-apotek tracking-widest bg-[#233855] text-white rounded-lg">ZAPISZ</button>
                      <button onClick={() => setShowUsernameForm(false)} className="px-4 py-2 text-xs font-apotek tracking-widest bg-white border border-gray-200 rounded-lg text-gray-600">ANULUJ</button>
                    </div>
                  </div>
                )}

                {/* Hasło */}
                {!showPasswordForm ? (
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <p className="text-sm text-gray-600">Hasło: <strong className="text-[#233855]">••••••••</strong></p>
                    <button onClick={() => setShowPasswordForm(true)} className="text-xs font-semibold text-[#233855] hover:underline">Edytuj</button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitPassword} className="space-y-2 pt-2 border-t border-gray-100">
                    <PasswordInput placeholder="Aktualne hasło" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} customClass="bg-gray-50 border border-gray-200" />
                    <PasswordInput placeholder="Nowe hasło" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); validatePassword(e.target.value); }} customClass="bg-gray-50 border border-gray-200" />
                    <PasswordInput placeholder="Powtórz nowe hasło" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} customClass="bg-gray-50 border border-gray-200" />
                    {passwordHints.length > 0 && (
                      <p className="text-[11px] text-[#8A0000]">{passwordHints.join(" ")}</p>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-xs font-apotek tracking-widest bg-[#233855] text-white rounded-lg disabled:opacity-50">{isSubmitting ? "ZAPISUJĘ..." : "ZAPISZ"}</button>
                      <button type="button" onClick={() => setShowPasswordForm(false)} className="px-4 py-2 text-xs font-apotek tracking-widest bg-white border border-gray-200 rounded-lg text-gray-600">ANULUJ</button>
                    </div>
                  </form>
                )}

                {/* Strefa niebezpieczna */}
                <div className="pt-3 mt-2 border-t border-red-100">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#8A0000] mb-1">Strefa niebezpieczna</p>
                  <p className="text-xs text-gray-500 mb-3">Usunięcie konta jest trwałe i nieodwracalne.</p>
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className="px-4 py-2 text-xs font-apotek tracking-widest rounded-lg bg-[#8A0000] text-white hover:bg-[#700000] transition-colors"
                  >
                    USUŃ KONTO
                  </button>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          {/* Wyloguj */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-xs font-apotek tracking-widest text-gray-500 shadow-sm hover:bg-gray-50 hover:text-[#233855] hover:border-[#233855]/20 active:scale-[0.98] transition-all"
          >
            <MdLogout className="h-4 w-4" /> WYLOGUJ SIĘ
          </button>

        </div>
        <div className="h-[13vh] flex-shrink-0" />
      </div>
      </div>

      <SuccessModal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} newExpiryDate={modalExpiryDate} />
      <DeleteAccountModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} />
      <MarketingConsentModal isOpen={isMarketingModalOpen} onClose={() => setIsMarketingModalOpen(false)} onConfirm={handleConfirmMarketing} isEnabling={!emailMarketingConsent} isLoading={isMarketingLoading} />
    </Page>
  );
};

export default UserSettings;
